// src/components/InlineNewPropertyCard.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Send,
  Building2,
  Mail,
  Bot,
  Sparkles,
  X,
  RefreshCw,
  Check,
  MapPin,
  ExternalLink
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../firebase';
import { deleteField, doc, getDoc, runTransaction, updateDoc, serverTimestamp } from 'firebase/firestore';
import { injectNameIntoScript, normalizeScriptWithPlaceholder, stripSignOff } from '../utils/emailPreview';
import { safeGreetingFirstName } from '../utils/greetingName';
import { deriveAskFields } from '../utils/campaignAskFields';
import { validateCampaignLaunchDraft } from '../utils/draftGuards';
import { createActionAuditEntry, prepareActionAuditOutboxBatch } from '../utils/actionAudit';
import { deleteNotificationWithCounters } from '../utils/notificationCounters';
import { buildActiveSignatureFromProfile } from '../utils/signatureBuilder';
import ConfirmDialog from './ConfirmDialog';
import SignaturePreview from './SignaturePreview';
import '../styles/InlineNewPropertyCard.css';
import { plainAskLabels } from '../utils/plainAskLabels';

function suggestedPropertyIdentity(meta) {
  const city = String(meta.city || '').trim();
  let address = String(meta.address || '').trim();
  let propertyName = String(meta.propertyName || meta.suite || '').trim();
  // Legacy suggestions sometimes put the suite inside an address/city label.
  // Extract only one explicit suite token; separate reviewed fields remain the
  // authority when the operator corrects a suggestion.
  const suites = address.match(/\b(?:suite|ste\.?|unit)\s*#?\s*[a-z0-9]+(?:[-/][a-z0-9]+)*/gi) || [];
  if (!propertyName && suites.length === 1) propertyName = suites[0].trim();
  if (suites.length === 1 && propertyName.toLowerCase() === suites[0].trim().toLowerCase()) {
    address = address.replace(suites[0], '').replace(/\(\s*\)/g, '').trim().replace(/,\s*$/, '');
  }
  if (city && address.toLowerCase().endsWith(`, ${city.toLowerCase()}`)) {
    address = address.slice(0, -(city.length + 2)).trim();
  }
  return { address: address.replace(/,\s*$/, '').trim(), propertyName, city };
}

function propertySubject(property) {
  return [property.address, property.propertyName, property.city].filter(Boolean).join(', ');
}

function samePropertyIdentity(left, right) {
  const normalize = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  return left && ['address', 'propertyName', 'city'].every(key => normalize(left[key]) === normalize(right[key]));
}

/**
 * Inline card for new property suggestions.
 * Shows after the message where broker suggested a new property.
 * Creates a NEW conversation thread (not a reply).
 */
export default function InlineNewPropertyCard({
  notification,
  client,
  onSend,
  onDismiss
}) {
  const textareaRef = useRef();
  const chatInputRef = useRef();
  const chatMessagesRef = useRef();

  // Email state
  const [subject, setSubject] = useState('');
  const [reviewedProperty, setReviewedProperty] = useState({ address: '', propertyName: '', city: '' });
  const [body, setBody] = useState('');
  const [recipient, setRecipient] = useState('');
  const [emailSignature, setEmailSignature] = useState(null);
  const [signatureMode, setSignatureMode] = useState(null);
  const [professionalSignature, setProfessionalSignature] = useState(null);

  // AI Chat state
  const [showAiChat, setShowAiChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [showEmailUpdatedBadge, setShowEmailUpdatedBadge] = useState(false);

  // Send state
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null); // 'creating_row' | 'queuing' | 'done'
  const [draftErrors, setDraftErrors] = useState([]);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const [attachmentReviewPending, setAttachmentReviewPending] = useState(false);

  // Get notification context
  const meta = useMemo(() => notification?.meta || {}, [notification?.meta]);
  const propertyInfo = {
    address: meta.address || '',
    city: meta.city || '',
    link: meta.link || '',
    notes: meta.notes || '',
    brokerEmail: meta.brokerEmail || '',
    contactName: meta.contactName || meta.leasingContact || '',
    leasingCompany: meta.leasingCompany || '',
    sheetId: meta.sheetId || '',
    tabTitle: meta.tabTitle || '',
    pdfLinks: meta.pdfLinks || [],
    pdfManifest: meta.pdfManifest || [],  // Full PDF data for AI extraction
    propertyImages: meta.propertyImages || []
  };
  const isSameContactReplacement = meta.isDifferentContact === false;
  const hasUnboundFlyers = Array.isArray(propertyInfo.pdfManifest) && propertyInfo.pdfManifest.length > 0;
  const hasAskContract = Boolean(client?.columnConfig) || Array.isArray(client?.askFields);
  const askFields = useMemo(() => (
    Array.isArray(client?.askFields)
      ? client.askFields
      : deriveAskFields(client?.columnConfig)
  ), [client?.askFields, client?.columnConfig]);

  // Get contact's first name for the composer's placeholder hint. It suggests
  // the opening line the operator is about to type, so it answers to the same
  // greeting rule as the copy that actually sends — otherwise the card would
  // still be proposing "Hi Not," for a contact cell reading "Not provided".
  const contactFirstName = useMemo(
    () => safeGreetingFirstName(propertyInfo.contactName),
    [propertyInfo.contactName]
  );

  // Fetch user's email signature
  useEffect(() => {
    async function fetchSignature() {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setEmailSignature(data.emailSignature || null);
          setSignatureMode(data.signatureMode || null);
          setProfessionalSignature(data.professionalSignature || null);
        }
      } catch (err) {
        console.error('Failed to fetch signature:', err);
      }
    }
    fetchSignature();
  }, []);

  const previewSignature = useMemo(() => buildActiveSignatureFromProfile({
    emailSignature,
    signatureMode,
    professionalSignature,
    email: auth.currentUser?.email || ''
  }), [emailSignature, signatureMode, professionalSignature]);

  // Initialize from notification
  useEffect(() => {
    if (!notification) return;

    // Set recipient
    const brokerEmail = meta.brokerEmail || meta.suggestedEmail?.to?.[0] || '';
    setRecipient(brokerEmail);

    const identity = suggestedPropertyIdentity(meta);
    setReviewedProperty(identity);
    setSubject(propertySubject(identity));

    // Set body from suggested email or generate default
    if (meta.suggestedEmail?.body) {
      let emailBody = normalizeScriptWithPlaceholder(meta.suggestedEmail.body);
      // Inject name if we have contact name
      if (propertyInfo.contactName) {
        emailBody = injectNameIntoScript(emailBody, propertyInfo.contactName);
      }
      setBody(emailBody);
    } else {
      // Generate default outreach email. The first name comes from the one
      // shared greeting rule, never from `contactName.split(' ')[0]` — that is
      // what put "Hi Not," in front of a broker on 2026-09-22 when the contact
      // cell read "Not provided". `[NAME]` stays the fallback, as before: it is
      // the placeholder the operator resolves before the card can send.
      const firstName = safeGreetingFirstName(propertyInfo.contactName) || '[NAME]';
      // Plain words, never the sheet heading ("- Rent/SF /Yr" reached brokers
      // on 21 Sep); the same mapping the server's opening uses.
      const askLines = plainAskLabels(askFields).map(label => `- ${label}`).join('\n');
      const requestCopy = askLines
        ? `Could you confirm the current availability and provide:\n\n${askLines}`
        : 'Could you confirm the current availability?';
      const defaultBody = `Hi ${firstName},

I came across ${propertyInfo.address}${propertyInfo.city ? ` in ${propertyInfo.city}` : ''} and wanted to reach out.

I'm assisting a client who is searching for industrial space in the area. ${requestCopy}

Thanks`;
      setBody(defaultBody);
    }

    // Initialize AI chat
    setChatMessages([{
      role: 'assistant',
      content: `I've drafted an outreach email for the new property at **${propertyInfo.address}**.\n\nWould you like me to adjust the message? I can make it shorter, more formal, or add specific questions.`
    }]);
  }, [notification, meta, propertyInfo.address, propertyInfo.city, propertyInfo.contactName, askFields]);

  // Auto-resize textarea
  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };

  useEffect(() => {
    autoResizeTextarea();
  }, [body]);

  // Scroll chat to bottom
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Handle body change
  const handleBodyChange = (newValue) => {
    setBody(newValue);
    if (draftErrors.length > 0) {
      setDraftErrors([]);
    }
  };

  // AI Chat
  const sendChatMessage = async () => {
    if (!chatInput.trim() || isChatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsChatLoading(true);

    try {
      const functions = getFunctions();
      const chatFn = httpsCallable(functions, 'chatWithPropertyContext');

      const result = await chatFn({
        message: userMessage,
        propertyInfo: {
          address: propertyInfo.address,
          city: propertyInfo.city,
          link: propertyInfo.link,
          contactName: propertyInfo.contactName
        },
        conversationContext: {},
        currentEmailDraft: body,
        clientCriteria: client?.criteria || "",
        chatHistory: chatMessages.slice(-6),
        notificationContext: {
          reason: 'new_property_pending_approval',
          isNewProperty: true
        }
      });

      const assistantMessage = result.data?.message || 'I encountered an issue. Please try again.';
      const updatedEmail = result.data?.updatedEmail;

      setChatMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);

      if (updatedEmail) {
        setBody(updatedEmail);
        setShowEmailUpdatedBadge(true);
        setTimeout(() => setShowEmailUpdatedBadge(false), 3000);
        // Scroll to the textarea to show the updated email
        setTimeout(() => {
          textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  const quickSuggestions = [
    'Make it shorter',
    'More formal tone',
    'Ask about availability'
  ];

  // Send new property outreach
  const handleSend = async () => {
    if (isSending || !body.trim() || !recipient) return;

    const draftValidation = validateCampaignLaunchDraft(
      body,
      hasAskContract ? { askFields } : undefined
    );
    if (!draftValidation.ok) {
      setDraftErrors(draftValidation.errors);
      return;
    }

    setIsSending(true);
    setSendStatus('creating_row');
    setDraftErrors([]);

    try {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Not signed in');

      const { address: editedAddress, propertyName: editedPropertyName, city: editedCity } = reviewedProperty;
      if (!editedAddress.trim() || !editedCity.trim()) {
        throw new Error('Review the street address, suite and city before adding this property.');
      }
      const idToken = await auth.currentUser.getIdToken();

      const response = await fetch('https://us-central1-email-automation-cache.cloudfunctions.net/acceptNewProperty', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          clientId: client.id,
          notificationId: notification.id,
          propertyData: {
            address: editedAddress,
            propertyName: editedPropertyName,
            city: editedCity,
            link: propertyInfo.link,
            notes: propertyInfo.notes,
            leasingCompany: propertyInfo.leasingCompany,
            leasingContact: propertyInfo.contactName,
            brokerEmail: recipient,
            pdfLinks: propertyInfo.pdfLinks || [],
            pdfManifest: propertyInfo.pdfManifest || [],  // Full PDF data for AI extraction
            propertyImages: propertyInfo.propertyImages || []
          }
        })
      });

      const result = await response.json();
      if (!result.success) {
        throw Object.assign(new Error(result.error || 'Failed to create property row'), { code: 'acceptance_failed' });
      }

      const rowNumber = result.rowNumber;
      if (!Number.isInteger(rowNumber) || rowNumber < 3 ||
          result.notificationId !== notification.id ||
          !samePropertyIdentity(result.property, reviewedProperty) ||
          (isSameContactReplacement && result.sourceThreadId !== notification.threadId)) {
        throw Object.assign(new Error('The sheet did not confirm the selected property. Refresh and review before retrying.'), { code: 'replacement_identity_mismatch' });
      }

      const heldAttachmentReview = result.extractionStatus === 'held_unbound_attachment';
      if ((hasUnboundFlyers || heldAttachmentReview) && (!heldAttachmentReview || result.reviewNotificationId !== `${notification.id}__attachment_review` ||
          (isSameContactReplacement && !String(result.replyToMessageId || '').trim()))) {
        throw Object.assign(new Error('The flyer review or original reply target could not be confirmed. No email was queued. Refresh and review before retrying.'), { code: 'review_handoff_missing' });
      }
      setAttachmentReviewPending(heldAttachmentReview);

      if (isSameContactReplacement) {
        if (notification?.threadId) {
          const replacementThreadRef = doc(db, 'users', uid, 'threads', notification.threadId);
          await runTransaction(db, async transaction => {
            const snapshot = await transaction.get(replacementThreadRef);
            if (!snapshot.exists()) {
              throw Object.assign(
                new Error('The original conversation could not safely continue. Refresh before retrying.'),
                { code: 'replacement_terminal_conflict' }
              );
            }
            const current = snapshot.data() || {};
            const hasPriorPropertyDisposition = Boolean(current.nonViableAt);
            const status = String(current.status || '').trim().toLowerCase();
            const isCampaignOrConversationStop = (
              current.statusReason === 'terminal_stop' ||
              Boolean(current.stoppedAt) ||
              (['archived', 'cancelled', 'canceled', 'closed', 'completed', 'dismissed', 'stopped'].includes(status) &&
                !hasPriorPropertyDisposition)
            );
            if (
              current.clientId !== client.id ||
              current.optedOutAt ||
              isCampaignOrConversationStop
            ) {
              throw Object.assign(
                new Error('The original conversation could not safely continue. Refresh before retrying.'),
                { code: 'replacement_terminal_conflict' }
              );
            }
            const previousDisposition = {};
            [
              'status', 'statusReason', 'followUpStatus', 'nonViableAt',
              'nonViableReason', 'pendingTerminalReason', 'pendingTerminalAt'
            ].forEach(key => {
              if (current[key] !== undefined && current[key] !== null) {
                previousDisposition[key] = current[key];
              }
            });
            const existingPdfHold = (
              current.pdfManualReviewRequired === true ||
              current.pdfManualReviewCleanupRequired === true
            );
            const replacementMustStayPaused = heldAttachmentReview || existingPdfHold;
            transaction.update(replacementThreadRef, {
              rowNumber,
              subject: propertySubject(reviewedProperty),
              status: replacementMustStayPaused ? 'paused' : 'active',
              followUpStatus: heldAttachmentReview
                ? 'paused'
                : (existingPdfHold ? 'needs_review' : 'waiting'),
              statusReason: heldAttachmentReview
                ? 'needs_user_input:attachment_needs_manual_review'
                : (existingPdfHold
                  ? (current.statusReason || 'needs_user_input:attachment_needs_manual_review')
                  : 'same_contact_replacement'),
              nonViableAt: deleteField(),
              nonViableReason: deleteField(),
              pendingTerminalReason: deleteField(),
              pendingTerminalAt: deleteField(),
              activeReplacementProperty: {
                address: editedAddress,
                propertyName: editedPropertyName,
                city: editedCity,
                rowNumber,
                sourceNotificationId: notification.id,
                previousRowAnchor: notification?.rowAnchor || null,
                previousDisposition
              },
              updatedAt: serverTimestamp()
            });
          });
        }

        await createActionAuditEntry({
          uid,
          actionType: 'new_property_add_to_sheet',
          status: 'completed',
          client,
          notification,
          subject,
          finalBody: '',
          finalRecipients: [],
          rowAnchor: notification?.rowAnchor || `${editedAddress}${editedCity ? `, ${editedCity}` : ''}`,
          source: 'dashboard_new_property',
          reason: meta.reason || 'new_property_pending_approval',
          rowNumber
        });

        // Deliberately NOT returning here any more, and not deleting the
        // notification.
        //
        // This branch used to create the row, re-point the thread at it, delete
        // the action and stop. Nothing downstream ever asked this broker about
        // the property he had just offered -- and the product had already told
        // him, in its own words, that it would come back to him about it. So the
        // row sat on the advisor's sheet with every specification blank, above
        // the non-viable divider, reading like any other live property, and the
        // campaign then marked itself Complete while carrying it. Accepting is
        // the ONLY way a suggested replacement enters the sheet, so every
        // replacement any broker offered ended in that state.
        //
        // The original instinct was right and is kept: this broker is already
        // mid-conversation, so a second COLD outreach would be a duplicate. But
        // "not a duplicate cold email" is not "say nothing, ever". It falls
        // through to the same queueing step as any other acceptance, and the
        // outbox entry below carries the existing threadId, so what goes out is
        // a reply INSIDE the conversation he is already in. That also gives the
        // campaign real outstanding work, so it can no longer report itself
        // finished with an unworked row.
      }

      // Step 2: Create the outbox entry. For a NEW contact this starts a new
      // conversation (no threadId). For a same-contact replacement it continues
      // the existing one.
      setSendStatus('queuing');

      // Use user's exact text - don't normalize or replace names
      const finalBody = stripSignOff(body.trim());
      const queuedOutreachBatch = prepareActionAuditOutboxBatch({
        uid,
        entries: [{
          audit: {
            actionType: isSameContactReplacement
              ? 'new_property_replacement_outreach'
              : 'new_property_outreach',
            client,
            notification,
            subject,
            suggestedBody: meta.suggestedEmail?.body || '',
            suggestedRecipients: meta.suggestedEmail?.to || [],
            finalBody,
            finalRecipients: [recipient],
            source: 'dashboard_new_property',
            reason: meta.reason || 'new_property_pending_approval'
          },
          outbox: {
            assignedEmails: [recipient],
            script: finalBody,
            clientId: client.id,
            subject,
            rowNumber,
            contactName: propertyInfo.contactName || null,
            isPersonalized: true,
            scriptSelectionMode: 'exact',
            forceScript: true,
            notificationId: notification.id,
            notificationClientId: client.id,
            deleteNotificationOnSend: true,
            createdAt: serverTimestamp(),
            // A new contact gets a NEW thread (no threadId). A same-contact
            // replacement continues the thread the promise was made on.
            ...(isSameContactReplacement && notification?.threadId
              ? { threadId: notification.threadId, resumeThreadOnSend: !heldAttachmentReview,
                  ...(heldAttachmentReview ? { replyToMessageId: result.replyToMessageId } : {}) }
              : {}),
            property: {
              address: editedAddress,
              propertyName: editedPropertyName,
              city: editedCity
            }
          }
        }]
      });

      const [{ outboxRef }] = await queuedOutreachBatch.commit();

      if (!outboxRef?.id) {
        throw new Error('Queued outreach did not return an outbox id');
      }

      // Step 3: keep the action visible as pending until the backend worker sends the email
      await updateDoc(doc(db, 'users', uid, 'clients', client.id, 'notifications', notification.id), {
        'meta.reason': 'new_property_pending_send',
        'meta.status': 'pending_send',
        'meta.pendingOutboxId': outboxRef.id,
        'meta.rowNumber': rowNumber,
        updatedAt: serverTimestamp()
      });

      setSendStatus('done');

      // Notify parent after a brief delay to show success state
      setTimeout(() => {
        if (onSend) onSend();
      }, 1500);

    } catch (err) {
      console.error('Failed to complete new property action:', err);
      setDraftErrors([
        ['replacement_identity_mismatch', 'replacement_terminal_conflict', 'review_handoff_missing', 'acceptance_failed'].includes(err.code)
          ? err.message
          : isSameContactReplacement
            ? 'Failed to add this property to the sheet. Please try again.'
            : 'Failed to queue this outreach. Please try again.'
      ]);
      setIsSending(false);
      setSendStatus(null);
    }
  };

  // Dismiss the suggestion
  const handleDismiss = async () => {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      await createActionAuditEntry({
        uid,
        actionType: 'new_property_dismiss',
        status: 'dismissed',
        client,
        notification,
        subject,
        finalBody: '',
        finalRecipients: [],
        rowAnchor: notification?.rowAnchor || propertyInfo.address || null,
        source: 'dashboard_new_property',
        reason: meta.reason || 'new_property_pending_approval'
      });

      await deleteNotificationWithCounters({
        db,
        uid,
        clientId: client.id,
        notificationId: notification.id
      });

      if (onDismiss) onDismiss();
    } catch (err) {
      console.error('Failed to dismiss:', err);
      alert('Failed to dismiss. Please try again.');
    } finally {
      setShowDismissConfirm(false);
    }
  };

  // A same-contact acceptance now SENDS something, so it needs the same
  // preconditions as any other send. It used to be exempt because it sent
  // nothing -- which was the defect.
  const canSend = body.trim().length > 0 && recipient && reviewedProperty.address.trim() && reviewedProperty.city.trim() && !isSending;

  const getSendButtonText = () => {
    if (!isSending) return isSameContactReplacement ? 'Add Property & Reply' : 'Send Outreach';
    switch (sendStatus) {
      case 'creating_row': return isSameContactReplacement ? 'Adding property...' : 'Creating row...';
      case 'queuing': return 'Queuing email...';
      case 'done': return isSameContactReplacement ? 'Added & queued!' : 'Queued!';
      default: return 'Sending...';
    }
  };

  return (
    <div className="inline-new-property-card">
      {/* Header */}
      <div className="inpc-header">
        <div className="inpc-header-left">
          <Building2 size={18} className="inpc-header-icon" />
          <span className="inpc-header-title">New Property Suggested</span>
        </div>
        <button className="inpc-close-btn" onClick={handleDismiss} title="Dismiss">
          <X size={16} />
        </button>
      </div>

      {/* Property Info */}
      <div className="inpc-property-info">
        <div className="inpc-property-address">
          <MapPin size={14} />
          <span>{propertyInfo.address}{propertyInfo.city ? `, ${propertyInfo.city}` : ''}</span>
        </div>
        {propertyInfo.link && (
          <a href={propertyInfo.link} target="_blank" rel="noopener noreferrer" className="inpc-property-link">
            <ExternalLink size={12} />
            View Listing
          </a>
        )}
      </div>

      {isSameContactReplacement && (
        <div className="inpc-inline-note">
          Adds this replacement to the sheet and replies in the conversation you are
          already having with this broker - not a second cold email.
        </div>
      )}

      <div className="inpc-property-fields">
        {[
          ['address', 'Street address'],
          ['propertyName', 'Suite / property name'],
          ['city', 'City'],
        ].map(([key, label]) => (
          <label className="inpc-field" key={key}>
            <span className="inpc-label">{label}</span>
            <input
              type="text"
              className="inpc-input"
              value={reviewedProperty[key]}
              onChange={event => {
                const next = { ...reviewedProperty, [key]: event.target.value };
                setReviewedProperty(next);
                setSubject(propertySubject(next));
              }}
              required={key !== 'propertyName'}
            />
          </label>
        ))}
      </div>

      {/* Recipient */}
      <div className="inpc-field">
        <label className="inpc-label">To</label>
        <div className="inpc-recipient-row">
          <Mail size={14} />
          <input
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            className="inpc-input inpc-recipient-input"
            placeholder="broker@email.com"
          />
          {propertyInfo.contactName && (
            <span className="inpc-recipient-name">({propertyInfo.contactName})</span>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="inpc-composer">
        {/* Subject */}
        <div className="inpc-field">
          <label className="inpc-label">Subject</label>
          <input
            type="text"
            aria-label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="inpc-input"
            placeholder="Property address..."
          />
        </div>

        {/* Body with AI Help */}
        <div className="inpc-field inpc-field-body">
          <div className="inpc-body-header">
            <label className="inpc-label">Message</label>
            <button
              className={`inpc-ai-btn ${showAiChat ? 'active' : ''}`}
              onClick={() => setShowAiChat(!showAiChat)}
            >
              <Sparkles size={14} />
              {showAiChat ? 'Hide AI' : 'AI Help'}
            </button>
          </div>

          {/* AI Chat Panel */}
          {showAiChat && (
            <div className="inpc-chat-panel">
              <div className="inpc-chat-header">
                <Bot size={16} />
                <span>AI Assistant</span>
              </div>

              <div className="inpc-chat-messages" ref={chatMessagesRef}>
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`inpc-chat-message inpc-chat-${msg.role}`}>
                    <div className="inpc-chat-bubble">{msg.content}</div>
                  </div>
                ))}
                {isChatLoading && (
                  <div className="inpc-chat-message inpc-chat-assistant">
                    <div className="inpc-chat-bubble inpc-chat-loading">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                )}
              </div>

              <div className="inpc-chat-input-container">
                <input
                  ref={chatInputRef}
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleChatKeyDown}
                  placeholder="Ask AI for help..."
                  className="inpc-chat-input"
                  disabled={isChatLoading}
                />
                <button
                  onClick={sendChatMessage}
                  disabled={!chatInput.trim() || isChatLoading}
                  className="inpc-chat-send"
                >
                  {isChatLoading ? <RefreshCw size={14} className="spinning" /> : <Send size={14} />}
                </button>
              </div>

              <div className="inpc-quick-suggestions">
                {quickSuggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    className="inpc-quick-btn"
                    onClick={() => {
                      setChatInput(suggestion);
                      chatInputRef.current?.focus();
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => {
              handleBodyChange(e.target.value);
              autoResizeTextarea();
            }}
            className="inpc-textarea"
            placeholder={contactFirstName ? `Hi ${contactFirstName},\n\nEnter your outreach message...` : "Hi,\n\nEnter your outreach message..."}
          />

          {showEmailUpdatedBadge && (
            <div className="inpc-updated-badge">
              <Check size={12} />
              Email updated by AI
            </div>
          )}
          {draftErrors.length > 0 && (
            <div className="inpc-draft-errors" role="alert">
              {draftErrors.map(error => (
                <div key={error}>{error}</div>
              ))}
            </div>
          )}
        </div>

        {/* Signature */}
        <SignaturePreview
          signatureMode={signatureMode}
          emailSignature={previewSignature}
          isLoading={false}
          compact={true}
        />
      </div>

      {hasUnboundFlyers && (
        <p className="inpc-draft-errors" role="status">
          Flyer facts will not be copied automatically. A separate review card will link to the flyers and sheet.
          {isSameContactReplacement && ' Your exact reply will be queued in this conversation; automatic follow-ups will stay paused until the flyer review is handled.'}
        </p>
      )}

      {/* Actions */}
      <div className="inpc-actions">
        <button className="inpc-btn inpc-btn-dismiss" onClick={() => setShowDismissConfirm(true)} disabled={isSending}>
          Dismiss
        </button>
        <button
          className={`inpc-btn inpc-btn-send ${sendStatus === 'done' ? 'success' : ''}`}
          onClick={handleSend}
          disabled={!canSend}
        >
          {isSending && sendStatus !== 'done' && <RefreshCw size={14} className="spinning" />}
          {sendStatus === 'done' && <Check size={14} />}
          {!isSending && <Send size={14} />}
          {getSendButtonText()}
        </button>
      </div>

      {/* Success message */}
      {sendStatus === 'done' && (
        <div className="inpc-success-message">
          {attachmentReviewPending
            ? 'Reply queued. Flyer facts were not copied. Open the Attachment Needs Your Review card to review the flyers and sheet.'
            : isSameContactReplacement
              ? 'Reply queued in the original conversation.'
              : 'Email queued! A new conversation will appear when the scheduler sends it.'}
        </div>
      )}
      <ConfirmDialog
        open={showDismissConfirm}
        title="Dismiss this property suggestion?"
        description="It will not be added to the sheet."
        confirmLabel="Dismiss"
        onCancel={() => setShowDismissConfirm(false)}
        onConfirm={handleDismiss}
      />
    </div>
  );
}
